import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ListOrdered } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ContainerPagination from './ContainerPagination';
import DocsBreadcrumb from './DocsBreadcrumb';
import KeyboardShortcutsOverlay from './KeyboardShortcutsOverlay';
import BackToTop from './BackToTop';
import { useSearchHighlight } from '../hooks/useSearchHighlight';

const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/69qn1rnj_ABSTRACT%20DESIGN.png';

/**
 * Shared layout wrapper used by all API Reference container pages.
 *
 * Handles:
 *   - Fixed full-page white abstract background
 *   - Global site Header + Footer
 *   - "Back to API Reference" button
 *   - Sticky right-side Table-of-Contents sidebar (with active-section tracking + smooth scroll)
 *   - Prev/Next Container Pagination at the bottom of the main column
 *
 * Each page only needs to supply:
 *   - `currentId`       → the container ID (used by pagination)
 *   - `title` + `subtitle` → rendered in the hero card
 *   - `toc`             → array of { id, label, children?: [{ id, label }] }
 *   - `mainTestId`      → data-testid for the <main> container (e.g. "void-preauth-page")
 *   - `sidebarTestId`   → data-testid for the <aside> (e.g. "void-preauth-sidebar")
 *   - `children`        → the page sections
 */
const ContainerPageLayout = ({
  currentId,
  title,
  subtitle,
  titleBadge,
  signature,
  toc,
  mainTestId,
  sidebarTestId,
  children,
}) => {
  const navigate = useNavigate();
  // Hierarchical back-navigation: every detail page always returns to its
  // parent hub. No state-magic, no history-pop guessing — just a clean
  // predictable parent link the user can always trust.
  const backLabel = 'Back to API Reference';
  const backTarget = '/technical-documentation/api-reference';
  const flatIds = toc.flatMap((i) => [i.id, ...(i.children?.map((c) => c.id) || [])]);
  const [activeId, setActiveId] = useState(flatIds[0]);

  // Activates highlighting when arriving from the Cmd/Ctrl-K search modal
  useSearchHighlight();

  useEffect(() => {
    const handleScroll = () => {
      const offset = 140;
      let current = flatIds[0];
      for (const id of flatIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - offset <= 0) current = id;
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [flatIds]);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileTocOpen(false);
  };

  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  // Flat list of TOC items for the mobile collapsed pill
  const flatToc = toc.flatMap((item) =>
    item.children
      ? [{ id: item.id, label: item.label, level: 2 }, ...item.children.map((c) => ({ id: c.id, label: c.label, level: 3 }))]
      : [{ id: item.id, label: item.label, level: 2 }]
  );

  // Reusable TOC nav body (used by both desktop sticky and mobile collapsed)
  const renderTocNav = (idPrefix = '') => (
    <nav className="py-2">
      {toc.map((item) => (
        <div key={item.id}>
          <a
            href={`#${item.id}`}
            onClick={(e) => handleNavClick(e, item.id)}
            className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
              activeId === item.id
                ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
            }`}
            data-testid={`${idPrefix}toc-link-${item.id}`}
          >
            {item.label}
          </a>
          {item.children && (
            <div className="ml-3">
              {item.children.map((child) => (
                <a
                  key={child.id}
                  href={`#${child.id}`}
                  onClick={(e) => handleNavClick(e, child.id)}
                  className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                    activeId === child.id
                      ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                  }`}
                  data-testid={`${idPrefix}toc-link-${child.id}`}
                >
                  {child.label}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col scroll-smooth" data-testid={mainTestId}>
      <Header />
      <div className="h-20" />

      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${BG_IMAGE})` }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <DocsBreadcrumb section="api-reference" currentId={currentId} />
          <button
            onClick={() => navigate(backTarget)}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid={`${mainTestId}-back-button`}
          >
            <ArrowLeft size={18} />
            {backLabel}
          </button>

          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_256px] lg:gap-8">
            <main className="min-w-0" data-testid={`${mainTestId}-main`}>
              {/* Mobile collapsed TOC pill */}
              {flatToc.length > 0 && (
                <div className="lg:hidden mb-6 border border-gray-200 rounded-xl overflow-hidden bg-white/90 backdrop-blur-sm">
                  <button
                    type="button"
                    onClick={() => setMobileTocOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
                    aria-expanded={mobileTocOpen}
                    data-testid="mobile-toc-toggle"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#0a1929]">
                      <ListOrdered size={16} className="text-[#ef5b25]" />
                      On this page
                      <span className="text-gray-400 font-normal">({flatToc.length})</span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-gray-500 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileTocOpen && (
                    <div className="bg-white max-h-[60vh] overflow-y-auto" data-testid="mobile-toc-panel">
                      {renderTocNav('m-')}
                    </div>
                  )}
                </div>
              )}

              {/* Title card */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h1 className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid={`${mainTestId}-title`}>
                    {title}
                  </h1>
                  {titleBadge && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-[#1F4E79] text-white uppercase tracking-wide">
                      {titleBadge}
                    </span>
                  )}
                </div>
                {subtitle && <p className="text-gray-600 text-lg">{subtitle}</p>}
                {signature && (
                  <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="api-signature-chips">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                      API Signature{Array.isArray(signature) && signature.length > 1 ? 's' : ''}
                    </span>
                    {(Array.isArray(signature) ? signature : [signature]).map((sig) => (
                      <code
                        key={sig}
                        className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-900 text-[#85ea2d] text-xs font-mono font-semibold shadow-sm border border-gray-800"
                        data-testid={`api-signature-${sig}`}
                      >
                        APISignature={sig}
                      </code>
                    ))}
                  </div>
                )}
              </div>

              {/* Sections (supplied by parent) */}
              {children}

              <ContainerPagination currentId={currentId} />
            </main>

            {/* Sticky desktop TOC (hidden on mobile) */}
            <aside
              className="hidden lg:block lg:sticky lg:top-28 self-start max-h-[calc(100vh-7rem)] overflow-hidden rounded-lg shadow-md border border-gray-200 bg-white/85 backdrop-blur-sm"
              data-testid={sidebarTestId}
            >
              <div className="px-4 py-3 bg-[#1F4E79] text-white">
                <p className="text-sm font-semibold tracking-wide">On This Page</p>
              </div>
              <div className="max-h-[calc(100vh-10rem)] overflow-y-auto">{renderTocNav()}</div>
            </aside>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <KeyboardShortcutsOverlay />
      <BackToTop />
    </div>
  );
};

// Helper components exported for use inside container pages

export const CodeBlock = ({ code, lang = 'csharp', testid }) => (
  <div className="relative bg-[#1e1e1e] rounded-lg overflow-hidden border border-gray-700 my-4" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-gray-700">
      <span className="text-xs font-mono text-gray-400 uppercase tracking-wide">{lang}</span>
      <button
        onClick={() => navigator.clipboard?.writeText(code)}
        className="text-xs text-gray-400 hover:text-white transition-colors duration-150 font-medium"
        data-testid={`${testid}-copy`}
      >
        Copy
      </button>
    </div>
    <pre className="overflow-x-auto p-4 text-sm leading-relaxed">
      <code className={`language-${lang} text-gray-100 font-mono whitespace-pre`}>{code}</code>
    </pre>
  </div>
);

export const ReqBadge = ({ value }) => {
  const styles =
    value === 'Y'
      ? 'bg-green-100 text-green-800 border-green-200'
      : value === 'N'
      ? 'bg-gray-100 text-gray-700 border-gray-200'
      : 'bg-amber-100 text-amber-800 border-amber-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${styles}`}>
      {value}
    </span>
  );
};

export const DownloadButtons = () => (
  <div className="flex flex-wrap gap-2">
    <a
      href="/assets/ecomm-api.postman_collection.json"
      download="ecomm-api.postman_collection.json"
      className="inline-flex items-center gap-2 px-3 py-2 bg-[#ef5b25] hover:bg-[#d14f1f] text-white text-xs font-semibold rounded-md shadow-sm transition-colors duration-150"
      data-testid="download-postman-btn"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13.52 10.2l-2.9 2.9-.72-.73c-.14-.15-.38-.15-.52 0-.15.14-.15.38 0 .52l.98.99c.08.07.18.11.27.11.1 0 .19-.04.26-.11l3.16-3.16c.14-.15.14-.38 0-.52-.15-.15-.38-.15-.53 0zM12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z"/>
      </svg>
      Download Postman Collection
    </a>
    <a
      href="/assets/ecomm-api-openapi.yaml"
      download="ecomm-api-openapi.yaml"
      className="inline-flex items-center gap-2 px-3 py-2 bg-[#85ea2d] hover:bg-[#6ec723] text-gray-900 text-xs font-semibold rounded-md shadow-sm transition-colors duration-150"
      data-testid="download-openapi-btn"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
      </svg>
      Download OpenAPI Spec
    </a>
  </div>
);

export default ContainerPageLayout;
